/**
 * Who administers the customer currently being viewed.
 *
 * This lived as three identical copies — `CustomerAdminGuard`, the `Topbar`
 * and `MobileNavDrawer` each re-derived it inline. That is how it broke: the
 * check reads `customer_access` off the user, `authService.mapUserResponse`
 * quietly stopped copying that field across, and all three went false at once
 * while still *looking* correct. Every customer admin was shown "Access
 * Denied" on their own tenants and only Nodus staff got through.
 *
 * One function, one test.
 */

import type { Customer, User } from '@/contexts/auth/types';

/**
 * True when `user` holds an admin grant on `activeCustomer`.
 *
 * Strictly about the per-customer `admin` flag — staff and superuser are a
 * separate axis, handled by {@link canAdministerCustomer}, because "is this
 * their customer" and "is this Nodus staff" are different questions and
 * conflating them is what makes a guard hard to reason about.
 */
export const administersCustomer = (
  user: Partial<Pick<User, 'customer_access'>> | null | undefined,
  activeCustomer: Pick<Customer, 'id'> | null | undefined,
): boolean => {
  if (!activeCustomer) return false;
  const customerId = Number(activeCustomer.id);
  if (Number.isNaN(customerId)) return false;
  return !!user?.customer_access?.some((a) => a.customer_id === customerId && a.admin);
};

/**
 * True when `user` may use a customer-scoped admin surface.
 *
 * Nodus staff and superusers always may — they support every customer. A
 * customer admin may for their own customer.
 *
 * **Not a security boundary.** The backend's `@CustomerRequired()` is; this
 * only keeps the menu and the page agreeing about who a surface is for.
 */
export const canAdministerCustomer = (
  user: Partial<Pick<User, 'customer_access' | 'is_superuser' | 'is_staff'>> | null | undefined,
  activeCustomer: Pick<Customer, 'id'> | null | undefined,
): boolean => !!user?.is_superuser || !!user?.is_staff || administersCustomer(user, activeCustomer);
